import { useUserContext } from "@contexts/UserContext/index.context";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import type { Delivery as TDelivery } from "@types_def/models/delivery.types";
import { UserRole } from "@types_def/models/user.type";
import React, { useEffect, useRef, useState } from "react";

import type { TableStateType } from "../query";

import DeliveryTable from "./DeliveryTable";
import SearchOptions from "./SearchOptions";

function Delivery() {
  const [isLoading, setIsLoading] = useState(false);
  const user = useUserContext().userState;
  const isAdmin = user?.["custom:role"] === UserRole.ADMIN;
  const organizationId = user?.organizationId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [tableState, setTableState] = React.useState<TableStateType<TDelivery>>({
    tableFilter: {
      page: 0,
      order: "desc",
      orderBy: "id",
    },
  });
  const searchOptions = SearchOptions({ isAdmin, organizationId });
  const [data, setData] = useState<{
    records: TDelivery[];
    total: number;
  }>();
  const [search, setSearch] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Autofocus the search input when component mounts
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const keyword = formData.get("search") as string;

    if (!keyword || keyword.trim() === "") {
      setIsLoading(false);
      setData(undefined);
      return;
    }

    setIsLoading(true);
    searchOptions[search]
      .fn(keyword.trim(), tableState)
      .then((data) => {
        setData(data);
      })
      .catch((_err) => {
        setData(undefined);
      })
      .finally(() => setIsLoading(false));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Escape") {
      formRef.current?.reset();
      setData(undefined);
      // Refocus the search input after clearing
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  };

  const handlepageChange = (page: number) => {
    const searchInput = searchInputRef.current;
    const value = searchInput?.value;

    if (!value || value.trim() === "") {
      setIsLoading(false);
      setData(undefined);
      return;
    }

    setIsLoading(true);
    setTableState({ ...tableState, tableFilter: { ...tableState.tableFilter, page } });
    searchOptions[search]
      .fn(value.trim(), {
        ...tableState,
        tableFilter: { ...tableState.tableFilter, page },
      })
      .then((data) => {
        setData(data);
      })
      .catch((_err) => {
        setData(undefined);
      })
      .finally(() => setIsLoading(false));
  };

  const currentSearchOption = searchOptions[search];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {searchOptions.map((option) => (
          <Chip
            key={option.id}
            label={option.name}
            color="secondary"
            onClick={() => setSearch(option.id)}
            variant={option.id === search ? "filled" : "outlined"}
            size={isMobile ? "small" : "medium"}
          />
        ))}
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box>
        <Box component="form" onSubmit={handleSubmit} ref={formRef} onKeyDown={handleKeyDown}>
          <TextField
            ref={searchInputRef}
            name="search"
            type="text"
            placeholder={`Search by ${currentSearchOption.name.toLowerCase()}...`}
            fullWidth
            autoFocus
            autoComplete="off"
            size={isMobile ? "small" : "medium"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Press Enter to search or Escape to clear
        </Typography>
      </Box>
      <Divider sx={{ my: 2 }} />
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={isMobile ? 32 : 40} />
        </Box>
      ) : (
        <DeliveryTable
          data={data}
          handleChangePage={(_, page) => {
            handlepageChange(page);
          }}
          page={tableState.tableFilter.page}
        />
      )}
    </>
  );
}

export default Delivery;
